import React, { memo } from 'react';
import PropTypes from 'prop-types';

import { Link } from 'react-router-dom';

import {
  styled,
  Box,
} from '@mui/material';

import {
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@datanomik/commons-ui';

import { Lock } from '@mui/icons-material';

import { t } from 'i18next';

import { Can, useAbilityContext } from '@/contexts/ability';

import { MAIN_MENU_ITEMS, SETTINGS_MENU_ITEMS } from '@/assets/MenuItems';

import useMainMenu from './hooks';
import { styles } from './styles';

const MainMenuRaw = ({ className, isSettings }) => {
  const {
    activeRoute,
    createUrl,
  } = useMainMenu();

  const ability = useAbilityContext();

  const routes = isSettings ? SETTINGS_MENU_ITEMS : MAIN_MENU_ITEMS;

  return (
    <Box className={className}>
      <List sx={{ padding: 0 }}>
        {
          routes.map(({
            showInMenu,
            taskId,
            ...rest
          }) => ({
            showInMenu: showInMenu
              && (!taskId || ability.can('allowed', taskId.toString()))
              && (ability.can('enabled', rest.key)),
            ...rest,
          }))
            .filter(({ showInMenu }) => showInMenu)
            .map(({
              label,
              path,
              icon,
              key,
              link,
              type,
              isNew,
              isBeta,
              children,
            }) => {
              if (type === 'divider') {
                return (
                  <Can I="view" a={key} key={key}>
                    <Divider component="li" />
                  </Can>
                );
              }

              const isEnabled = ability.can('enabled', key);

              const [, ...allPath] = activeRoute;
              const isSelected = allPath.join('/').includes(path);

              let to = isEnabled ? link || createUrl(`/${path}`) : undefined;

              const subitems = (children?.filter(({ showInMenu }) => showInMenu) || [])
                .filter(({ path: subPath }) => subPath);
              const hasSubitems = subitems.length > 0;

              if (hasSubitems && to) {
                to = createUrl(`/${path}/${subitems[0].path}`);
              }

              const target = link ? '_blank' : '';

              return (
                <Can I="view" a={key} key={key}>
                  <ListItem>
                    <ListItemButton
                      selected={isSelected}
                      {...({
                        component: link ? 'a' : Link,
                        target,
                        ...(link
                          ? { href: link }
                          : { to }),
                      })}
                      disabled={!isEnabled}
                    >
                      {icon && (
                        <ListItemIcon>{icon}</ListItemIcon>
                      )}

                      <ListItemText primary={t(label)} />

                      {!isEnabled && !isNew && !isBeta && (
                        <ListItemIcon className="DnkListItemIcon-lock">
                          <Lock className="dn-menu-icon-lock" />
                        </ListItemIcon>
                      )}

                      {isNew && (
                        <Chip
                          label={t('New')}
                          sx={{ backgroundColor: 'teal.light', color: 'teal.darker' }}
                        />
                      )}

                      {isBeta && (
                        <Chip
                          label="Beta"
                          sx={{ backgroundColor: 'warning.lighter', color: 'warning.darker' }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                </Can>
              );
            })
        }
      </List>
    </Box>
  );
};

const MainMenu = styled(MainMenuRaw)(styles);

MainMenuRaw.propTypes = {
  className: PropTypes.string,
  isSettings: PropTypes.bool,
};

MainMenu.propTypes = MainMenuRaw.propTypes;

export default memo(MainMenu);
